(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
    typeof define === 'function' && define.amd ? define(['exports'], factory) :
    (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.Touchsweep = {}));
})(this, (function (exports) { 'use strict';

    /******************************************************************************
    Copyright (c) Microsoft Corporation.

    Permission to use, copy, modify, and/or distribute this software for any
    purpose with or without fee is hereby granted.

    THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
    REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
    AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
    INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
    LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
    OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
    PERFORMANCE OF THIS SOFTWARE.
    ***************************************************************************** */
    /* global Reflect, Promise, SuppressedError, Symbol */


    var __assign = function() {
        __assign = Object.assign || function __assign(t) {
            for (var s, i = 1, n = arguments.length; i < n; i++) {
                s = arguments[i];
                for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
            }
            return t;
        };
        return __assign.apply(this, arguments);
    };

    typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
        var e = new Error(message);
        return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
    };

    exports.TouchSwipeEventType = void 0;
    (function (TouchSwipeEventType) {
        TouchSwipeEventType["up"] = "swipeup";
        TouchSwipeEventType["tap"] = "tap";
        TouchSwipeEventType["down"] = "swipedown";
        TouchSwipeEventType["move"] = "swipemove";
        TouchSwipeEventType["left"] = "swipeleft";
        TouchSwipeEventType["right"] = "swiperight";
    })(exports.TouchSwipeEventType || (exports.TouchSwipeEventType = {}));
    var defaultCoordinates = {
        endX: 0,
        endY: 0,
        moveX: 0,
        moveY: 0,
        startX: 0,
        startY: 0
    };
    var TouchSweep = /** @class */ (function () {
        function TouchSweep(element, data, threshold) {
            if (element === void 0) { element = document.body; }
            if (data === void 0) { data = {}; }
            if (threshold === void 0) { threshold = 40; }
            this.element = element;
            this.eventData = data;
            this.threshold = threshold;
            this.coords = defaultCoordinates;
            this.isMoving = false;
            this.moveCoords = { x: 0, y: 0 };
            this.onStart = this.onStart.bind(this);
            this.onMove = this.onMove.bind(this);
            this.onEnd = this.onEnd.bind(this);
            this.bind();
            return this; //NOSONAR
        }
        TouchSweep.prototype.bind = function () {
            var element = this.element;
            element.addEventListener('touchstart', this.onStart, { passive: true });
            element.addEventListener('touchmove', this.onMove, { passive: true });
            element.addEventListener('touchend', this.onEnd, { passive: true });
            element.addEventListener('mousedown', this.onStart, { passive: true });
            element.addEventListener('mousemove', this.onMove, { passive: true });
            element.addEventListener('mouseup', this.onEnd, { passive: true });
        };
        TouchSweep.prototype.unbind = function () {
            var element = this.element;
            // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/removeEventListener#matching_event_listeners_for_removal
            element.removeEventListener('touchstart', this.onStart, false);
            element.removeEventListener('touchmove', this.onMove, false);
            element.removeEventListener('touchend', this.onEnd, false);
            element.removeEventListener('mousedown', this.onStart, false);
            element.removeEventListener('mousemove', this.onMove, false);
            element.removeEventListener('mouseup', this.onEnd, false);
        };
        TouchSweep.prototype.getCoords = function (event) {
            var result = this.moveCoords;
            var isMouseEvent = 'pageX' in event;
            result.x = isMouseEvent ? event.pageX : event.changedTouches[0].screenX;
            result.y = isMouseEvent ? event.pageY : event.changedTouches[0].screenY;
            return result;
        };
        TouchSweep.prototype.resetCoords = function () {
            this.coords = defaultCoordinates;
        };
        // prettier-ignore
        TouchSweep.prototype.getEndEventName = function () {
            var threshold = this.threshold;
            var _a = this.coords, startX = _a.startX, startY = _a.startY, endX = _a.endX, endY = _a.endY;
            var distanceX = Math.abs(endX - startX);
            var distanceY = Math.abs(endY - startY);
            var isSwipeX = distanceX > distanceY;
            if (isSwipeX) {
                if (endX < startX && distanceX > threshold) {
                    return exports.TouchSwipeEventType.left;
                }
                if (endX > startX && distanceX > threshold) {
                    return exports.TouchSwipeEventType.right;
                }
            }
            else {
                if (endY < startY && distanceY > threshold) {
                    return exports.TouchSwipeEventType.up;
                }
                if (endY > startY && distanceY > threshold) {
                    return exports.TouchSwipeEventType.down;
                }
            }
            if (endY === startY && endX === startX) {
                return exports.TouchSwipeEventType.tap;
            }
            return '';
        };
        TouchSweep.prototype.dispatchEvent = function (type) {
            var event = new CustomEvent(type, {
                detail: __assign(__assign({}, this.eventData), { coords: this.coords })
            });
            this.element.dispatchEvent(event);
        };
        TouchSweep.prototype.dispatchEnd = function () {
            var eventName = this.getEndEventName();
            if (!eventName) {
                return;
            }
            this.dispatchEvent(eventName);
        };
        TouchSweep.prototype.onStart = function (event) {
            var _a = this.getCoords(event), x = _a.x, y = _a.y;
            this.isMoving = true;
            this.coords.startX = x;
            this.coords.startY = y;
        };
        TouchSweep.prototype.onMove = function (event) {
            if (!this.isMoving) {
                return;
            }
            var _a = this.getCoords(event), x = _a.x, y = _a.y;
            this.coords.moveX = x;
            this.coords.moveY = y;
            this.dispatchEvent(exports.TouchSwipeEventType.move);
        };
        TouchSweep.prototype.onEnd = function (event) {
            var _a = this.getCoords(event), x = _a.x, y = _a.y;
            this.isMoving = false;
            this.coords.endX = x;
            this.coords.endY = y;
            this.dispatchEnd();
            this.resetCoords();
        };
        return TouchSweep;
    }());

    exports.default = TouchSweep;

    Object.defineProperty(exports, '__esModule', { value: true });

}));
//# sourceMappingURL=touchsweep.js.map
